import {Pagination} from 'platform/components';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {INLINE_EDGE_INSET} from '../../constants/INLINE_EDGE_INSET';
import {useIsMobileView} from '../../hooks/useIsMobileView';
import type {PaginationDisplayMode} from '../../types/Api';
import {ResultSummary} from '../ResultSummary/ResultSummary';
import {InputModePagination} from './components/InputModePagination/InputModePagination';

const DESKTOP_ROW_CLASS_NAME =
  'flex items-center justify-between gap-3 flex-wrap border-t border-gray-200 bg-white relative z-(--z-header-popper)';
// Same as the py-2.5 top padding of the CARD row: the gap between the table and
// the pagination row.
const TABLE_GAP = '0.625rem';
// An INLINE grid on desktop keeps the gap to the table, but only INLINE_EDGE_INSET
// at the grid edges, so the result range and the page buttons line up with the
// table edges.
const INLINE_ROW_STYLE = {
  paddingTop: TABLE_GAP,
  paddingInline: INLINE_EDGE_INSET,
  paddingBottom: INLINE_EDGE_INSET,
} as const;

export type PaginationProps = {
  /** Zero-based page index (matches store `page`). */
  currentPage: number;
  navigationOnly?: boolean;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  /** How the page navigation renders: numbered buttons (default) or a page input. */
  paginationDisplayMode?: PaginationDisplayMode;
  /** Result of useIsInlineDesktop: the row keeps only INLINE_EDGE_INSET at the grid edges. */
  isInlineDesktop?: boolean;
  onPageChange: (page: number) => void;
} & TestIdProps;

export function GridPagination(props: PaginationProps) {
  const isMobile = useIsMobileView();

  const isInputMode = props.paginationDisplayMode === 'INPUT';

  if (props.totalCount === 0) {
    return null;
  }

  // Shared by the mobile and desktop layouts below (2-way mode branch).
  // INPUT mode owns its draft/debounce/clamp state inside InputModePagination
  // (deletable value, clamped commits, 300ms debounce); BUTTONS mode stays
  // fully instant. Both use 1-based pages; store `currentPage` is 0-based.
  const pageNavigation = isInputMode ? (
    <InputModePagination
      page={props.currentPage + 1}
      pagesQuantity={props.totalPages}
      onPageChange={props.onPageChange}
      data-testid={suffixTestId('pagination', props)}
    />
  ) : (
    <Pagination
      page={props.currentPage + 1}
      pagesQuantity={props.totalPages}
      onPageChange={props.onPageChange}
      showPrevButton
      showNextButton
      data-testid={props['data-testid']}
    />
  );

  if (props.navigationOnly) {
    return props.totalPages > 1 ? pageNavigation : null;
  }

  // bg-white + relative + z-(--z-header-popper) keeps the pagination row above
  // the slide-out sidebar panel (which uses the same z var). Without this, the
  // sidebar's shadow-lg bleeds ~25px downward and visually overlaps the
  // pagination buttons — see T20-118961. DOM order (pagination comes after the
  // sidebar's row) breaks the tie when z-indexes are equal.
  if (isMobile) {
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap px-3.5 py-2 border-t border-gray-200 bg-white relative z-(--z-header-popper)">
        <ResultSummary
          currentPage={props.currentPage}
          itemsPerPage={props.itemsPerPage}
          totalCount={props.totalCount}
          variant="range"
          data-testid={suffixTestId('pagination', props)}
        />
        {props.totalPages > 1 && pageNavigation}
      </div>
    );
  }

  return (
    <div
      className={
        props.isInlineDesktop ? DESKTOP_ROW_CLASS_NAME : `${DESKTOP_ROW_CLASS_NAME} px-4 py-2.5`
      }
      style={props.isInlineDesktop ? INLINE_ROW_STYLE : undefined}
      data-testid={suffixTestId('paginationRow', props)}
    >
      {/* LEFT: result range info */}
      <ResultSummary
        currentPage={props.currentPage}
        itemsPerPage={props.itemsPerPage}
        totalCount={props.totalCount}
        variant="range"
        data-testid={suffixTestId('pagination', props)}
      />

      {/* RIGHT: page navigation */}
      <div className="flex items-center justify-end">{props.totalPages > 1 && pageNavigation}</div>
    </div>
  );
}
